import { DataAggregationFunction, DataDisplayOptions, DataFieldUsage, DataFieldUsageHints, DataSortMode, SchemaFieldTargetDto, SortDirection, TimestampPrecision } from 'src/app/services/api.services';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { DataAggregation, DataFormat, DataModel, DataSchema, DataSchemaItem } from '../dataModel';
import { DataType } from '../dataModel/DataType';
import { RichTextValue } from '../RichTextValue';
import { VisualCustomField } from './VisualCustomField';
import { CustomFieldIssue } from './customFields/CustomFieldIssue';
import { computed } from '@angular/core';

export abstract class VisualFieldTarget {
  readonly referenceString = computed(() => {
    return [this.identifierExpr, this.aggregation?.name ?? 'unaggregated'].join('.');
  });

  /**
   * Gets the schema item this target is associated with.
   * If the target is not associated with a schema item, returns null. */
  abstract get schemaItem(): DataSchemaItem | null;

  /**
   * Gets the custom field this target is associated with.
   * If the target is not associated with a custom field, returns null. */
  abstract get customField(): VisualCustomField | null;

  abstract get parent(): VisualFieldTarget | null;
  get isChild() { return !!this.parent; }
  abstract withChildField(childFieldName: string): VisualFieldTarget;

  /**
   * Gets a copy of this target with the specified aggregation.
   * If the specified aggregation is the same as the original, returns this target. */
  abstract withAggregation(aggregation: DataAggregation | null);

  /**
   * Whether this target can receive a new aggregation.
   * Fields with predefined aggregations (e.g. custom field `SUM(...)`) cannot change aggregations. */
  abstract get canAssignAggregation(): boolean;
  
  withDefaultAggregation() {
    if (!this.canAssignAggregation)
      throw new Error(`Target cannot be aggregated`, { cause: this });

    let aggregation =
      this.schemaItem?.getDefaultAggregation()
        ?? this.unaggregatedDataType.defaultAggregation;

    if (!aggregation)
      return this;

    return this.withAggregation(aggregation);
  }
  
  /** Whether this target must be aggregated (e.g. measures). */
  abstract get mustHaveAggregation(): boolean;


  /**
   * Gets an alternative version of this target.
   * The alternative depends on whether this target is aggregated.
   * An aggregated target that can be unaggregated will return such a copy.
   * An unaggregated target that can be aggregated will return such a copy with default aggregation.
   * If the target cannot change its aggregation, will return itself. */
  getAlternative(aggregated?: boolean): VisualFieldTarget {
    if (!this.canAssignAggregation)
      return this;

    if (isNullOrUndefined(aggregated))
      aggregated = !this.isAggregated;

    if (this.isAggregated) {
      if (aggregated)
        return this;

      return this.withAggregation(null);
    }
    else {
      if (!aggregated)
        return this;

      return this.withDefaultAggregation();
    }
  }


  //#region Display
  abstract get icon(): string;
  abstract get shortTitle(): string;
  abstract get longTitle(): string;
  abstract get description(): RichTextValue | null;
  abstract get tooltip(): RichTextValue | null;

  /** Formats long title of this display. */
  abstract formatLongTitle(separator?: string): string;

  get measureTitle() {
    return this.aggregation?.formatAggregationOfText(this.shortTitle) ?? this.shortTitle;
  }
  //#endregion

  //#region Source
  abstract get schema(): DataSchema;

  abstract get isValid(): boolean;
  abstract getErrorText(): string;

  abstract get unaggregatedDataType(): DataType;
  get dataType(): DataType { 
    let dataType = this.unaggregatedDataType;

    if (this.isAggregated) {
      let aggregation = this.aggregation;
      if (aggregation)
        return aggregation.getResultDataType(dataType);
    }

    return dataType; 
  }

  abstract get dataFormat(): DataFormat;
  abstract get dataDisplay(): DataDisplayOptions;

  abstract get aggregation(): DataAggregation | null;
  abstract get isAggregated(): boolean;

  abstract get hasCurrencyVariants(): boolean;
  abstract get isUDF(): boolean;

  abstract get itemGroupName(): string;
  abstract get identifierName(): string;
  abstract get identifierExpr(): string;
  abstract get schemaFieldTargetDto(): SchemaFieldTargetDto;

  hasTimestampPrecision(precision: TimestampPrecision) {
    if (DataFormat.TimestampPrecision.gte(precision, TimestampPrecision.Hours)) {
      if (!this.dataType.isTimeOrDateTime)
        return false;
    }
    else if (DataFormat.TimestampPrecision.lt(precision, TimestampPrecision.Hours)) {
      if (!this.dataType.isTimeOnly)
        return false;
    }
     
    return DataFormat.TimestampPrecision.gte(this.dataFormat?.timestamp ?? TimestampPrecision.Default, precision);
  }
  //#endregion

  /** Checks whether this target is the same source as another target. */
  abstract sameSource(other: VisualFieldTarget);

  /** Checks whether this target equals another target (i.e. same source and aggregation). */
  abstract equals(other: VisualFieldTarget);
  

  /**
   * Gets default sort direction for this target.
   * This will be its schema item's default sort when assigned.
   * Otherwise it will be the default sort for its data type. */
  get defaultSortDirection() {
    let sort = this.schemaItem?.field.defaultSort;
    if (sort == DataSortMode.Ascending) {
      return SortDirection.Ascending;
    }
    else if (sort == DataSortMode.Descending) {
      return SortDirection.Descending;
    }

    return this.dataType.defaultSortDirection;
  }


  get defaultUsage() {
    return this.schemaItem?.getDefaultUsage()
      ?? (this.isAggregated ? DataFieldUsage.Measure : DataFieldUsage.Grouping);
  }


  get usageHints(): DataFieldUsageHints {
    return this.schemaItem?.field?.usageHints;
  }
}

export class SchemaItemFieldTarget extends VisualFieldTarget {
  constructor(
    public readonly schemaItem: DataSchemaItem,
    public readonly aggregation: DataAggregation | null = null) {
    if (aggregation && schemaItem.field.isValueAggregated) {
      throw new Error(
        `Cannot create aggregated target for an already aggregated schema item`,
        { cause: schemaItem });
    }

    super();
  }

  get customField(): VisualCustomField | null { return null; }

  get parent() { return this.schemaItem.parent?.createVisualFieldTarget(); }

  withChildField(childFieldName: string): SchemaItemFieldTarget {
    if (!!this.schemaItem.parent)
      throw new Error(`The field is already a child field`);

    if (this.isAggregated)
      throw new Error(`Cannot get child field of an aggregation`);

    let child = this.schemaItem.children.get(childFieldName);
    if (!child)
      throw new Error(`Missing child field '${childFieldName}' of '${this.schemaItem.formatLongTitle('.')}'`);

    return new SchemaItemFieldTarget(child, this.aggregation);
  }

  withAggregation(aggregation: DataAggregation | null) {
    if (aggregation == this.aggregation)
      return this;

    if (!this.canAssignAggregation)
      throw new Error(`Cannot assign aggregation to this target`);

    return new SchemaItemFieldTarget(this.schemaItem, aggregation);
  }

  get canAssignAggregation(): boolean { return !this.schemaItem.field.isValueAggregated; }

  get mustHaveAggregation(): boolean { return this.schemaItem.field.effectiveMustHaveAggregation; }

  //#region Display
  get icon() { return this.schemaItem.getEditorIcon(); }
  get shortTitle() { return this.formatShortTitle(' - '); }
  get longTitle() { return this.formatLongTitle(' - '); }
  get description() { return this.schemaItem.itemDisplay.description; }
  get tooltip() { return this.schemaItem.itemDisplay.tooltip ?? RichTextValue.text(this.longTitle); }

  formatShortTitle(separator?: string): string {
    return this.schemaItem.formatShortTitle(separator);
  };

  formatLongTitle(separator?: string): string {
    return this.schemaItem.formatLongTitle(separator);
  }
  //#endregion

  //#region Source
  get schema(): DataSchema { return this.schemaItem.schema; }

  get isValid(): boolean { return !this.schemaItem.field.hasErrors; }
  getErrorText(): string { return null; }

  get unaggregatedDataType(): DataType { return this.schemaItem.field.dataType; }
  get dataFormat(): DataFormat { return this.schemaItem.field.effectiveFormat; }
  get dataDisplay(): DataDisplayOptions { return this.schemaItem.field.effectiveDisplayOptions; }

  get isAggregated(): boolean {
    return this.schemaItem.field.isValueAggregated || !!this.aggregation;
  }

  get hasCurrencyVariants(): boolean { return !!this.schemaItem.field.currencyVariants.size; }
  get isUDF(): boolean { return this.schemaItem.field.isUDF; }

  get itemGroupName(): string { return this.schemaItem.itemGroup.name; }
  get identifierName(): string { return this.schemaItem.getIdentifierName(); }
  get identifierExpr(): string { return this.schemaItem.getIdentifierExpr(); }

  get schemaFieldTargetDto(): SchemaFieldTargetDto {
    return {
      ...this.schemaItem.createSchemaFieldTargetDto(),
      aggregation: this.aggregation?.value
    };
  }
  //#endregion

  sameSource(other: VisualFieldTarget) {
    return !isNullOrUndefined(other) && (
      (other.schemaItem === this.schemaItem) ||
      SchemaUtils.sameField(other.schemaFieldTargetDto, this.schemaFieldTargetDto)
    );
  }

  equals(other: VisualFieldTarget) {
    return !isNullOrUndefined(other) && (this === other || (this.sameSource(other) && other.aggregation == this.aggregation));
  }
}

export class VisualCustomFieldTarget extends VisualFieldTarget {
  constructor(
    public readonly customField: VisualCustomField,
    public readonly aggregation: DataAggregation | null = null) {
    if (aggregation && customField.isAggregated()) {
      throw new Error(
        `Cannot create aggregated target for an already aggregating custom field`,
        { cause: customField });
    }

    super();
  }

  get schemaItem(): DataSchemaItem | null { return null; }

  get parent() { return this.customField.parent?.target(); }

  withChildField(childFieldName: string): VisualCustomFieldTarget {
    if (!!this.customField.parent)
      throw new Error(`The field is already a child field`);

    if (this.isAggregated)
      throw new Error(`Cannot get child field of an aggregation`);

    childFieldName = childFieldName.toLowerCase();

    let child = this.customField.children().find(f => f.name().toLowerCase() == childFieldName);
    if (!child)
      throw new Error(`Missing child field '${childFieldName}' of '${this.customField.formatLongTitle('.')}'`);

    return new VisualCustomFieldTarget(child, this.aggregation);
  }

  withAggregation(aggregation: DataAggregation | null) {
    if (aggregation == this.aggregation)
      return this;

    if (!this.canAssignAggregation)
      throw new Error(`Cannot assign aggregation to this target`);

    return new VisualCustomFieldTarget(this.customField, aggregation);
  }

  get canAssignAggregation(): boolean { return !this.customField.isAggregated(); }
  
  get mustHaveAggregation(): boolean { return false; }

  //#region Display
  get icon() { return this.customField.editorIcon(); }
  get shortTitle() { return this.customField.name(); }
  get longTitle() { return this.formatLongTitle(' - '); }
  get description() { return RichTextValue.empty; }
  get tooltip() { return RichTextValue.text(this.longTitle); }

  formatLongTitle(separator?: string): string {
    return this.customField.formatLongTitle(separator);
  }
  //#endregion

  //#region Source
  get schema(): DataSchema { return this.customField.context.schema; }

  get isValid(): boolean { return !this.customField.hasErrors(); }
  getErrorText(): string {
    return this.customField.issues()
      ?.filter(i => i.type == CustomFieldIssue.Type.Error)
      .map(i => i.message)
      .join('\n');
  }

  get unaggregatedDataType(): DataType { return this.customField.dataType(); }
  get dataFormat(): DataFormat { return this.customField.dataFormat(); }
  get dataDisplay(): DataDisplayOptions { return this.customField.dataDisplay(); }

  get isAggregated(): boolean {
    return this.customField.isAggregated() || !!this.aggregation;
  }

  get hasCurrencyVariants(): boolean { return !!this.customField.currencyVariants().size; }
  get isUDF(): boolean { return false; }

  get itemGroupName(): string { return VisualCustomField.GROUP_NAME; }
  get identifierName(): string { return this.customField.getIdentifierName(); }
  get identifierExpr(): string { return this.customField.getIdentifierExpr(); }

  get schemaFieldTargetDto(): SchemaFieldTargetDto {
    return {
      ...this.customField.createSchemaFieldTargetDto(),
      aggregation: this.aggregation?.value
    };
  }
  //#endregion


  sameSource(other: VisualFieldTarget) {
    return !isNullOrUndefined(other) && (
      (other.customField === this.customField) ||
      SchemaUtils.sameField(other.schemaFieldTargetDto, this.schemaFieldTargetDto)
    );
  }

  equals(other: VisualFieldTarget) {
    return !isNullOrUndefined(other) && (this === other || (this.sameSource(other) && other.aggregation == this.aggregation));
  }


  get usageHints() {
    return this.customField.usageHints();
  }
}

export class VisualInvalidTarget extends VisualFieldTarget {
  private _target: SchemaFieldTargetDto;

  constructor(schemaFieldTarget: SchemaFieldTargetDto) {
    super();

    this._target = deepClone(schemaFieldTarget);

    if (!this._target.aggregation)
      this._target.aggregation = DataAggregationFunction.DoNotSummarize;
  }

  get schemaItem() { return null; }
  get customField() { return null; }

  get parent() {
    return this._target.childField ? new VisualInvalidTarget({
      ...this.schemaFieldTargetDto,
      childField: null
    }) : null;
  }

  withChildField(childFieldName: string): VisualInvalidTarget {
    if (this._target.childField)
      throw new Error(`The field is already a child field`);

    if (this.isAggregated)
      throw new Error(`Cannot get child field of an aggregation`);

    return new VisualInvalidTarget({
      ...this.schemaFieldTargetDto,
      childField: childFieldName
    });
  }

  get aggregation() {
    return DataAggregation.from(this._target.aggregation);
  }

  withAggregation(aggregation: DataAggregation | null) {
    if (aggregation == this.aggregation)
      return this;

    if (!this.canAssignAggregation)
      throw new Error(`Cannot assign aggregation to this target`);

    return new VisualInvalidTarget({
      ...this._target,
      aggregation: aggregation?.value ?? DataAggregationFunction.DoNotSummarize
    });
  }

  get canAssignAggregation(): boolean { return true; }

  get mustHaveAggregation(): boolean { return false; }

  //#region Display
  get icon() { return 'fas fa-exclamation-triangle'; }

  get shortTitle() {
    return this._target.childField
      ? `${this._target.field} - ${this._target.childField}`
      : this._target.field;
  }

  get longTitle() { return this.formatLongTitle(' - '); }
  get description() { return RichTextValue.empty; }
  get tooltip() { return RichTextValue.text(this.longTitle); }

  formatLongTitle(separator?: string): string {
    return [this._target.group, this.shortTitle].join(separator ?? ' - ');
  }
  //#endregion

  //#region Source
  get schema(): DataSchema { return null; }

  get isValid(): boolean { return false; }
  getErrorText() { return `Missing field '${this.longTitle}'`; }

  get unaggregatedDataType(): DataType { return DataType.Unknown; }
  get dataFormat(): DataFormat { return { }; }
  get dataDisplay(): DataDisplayOptions { return { }; }

  get isAggregated(): boolean {
    return !!this.aggregation;
  }

  get hasCurrencyVariants(): boolean { return false; }
  get isUDF(): boolean { return false; }

  get itemGroupName(): string { return this._target.group; }

  get identifierName(): string {
    return [this._target.group, this._target.field, this._target.childField].filter(x => !!x).join('.');
  }

  get identifierExpr(): string {
    return `[${this.identifierName}]`;
  }

  get schemaFieldTargetDto(): SchemaFieldTargetDto {
    return deepClone(this._target);
  }
  //#endregion


  sameSource(other: VisualFieldTarget) {
    return !isNullOrUndefined(other) &&
           SchemaUtils.sameField(this.schemaFieldTargetDto, other.schemaFieldTargetDto);
  }

  equals(other: VisualFieldTarget) {
    return !isNullOrUndefined(other) && (this === other || (this.sameSource(other) && other.aggregation == this.aggregation));
  }
}

export namespace VisualFieldTarget {
  export function equal(a: VisualFieldTarget, b: VisualFieldTarget) {
    if (a === b)
      return true;

    if (isNullOrUndefined(a) || isNullOrUndefined(b))
      return isNullOrUndefined(a) && isNullOrUndefined(b);

    return a.equals(b);
  }

  export function fromSchemaFieldTarget(target: SchemaFieldTargetDto, dataModel: DataModel) {
    if (!target)
      return null;

    target = mapLegacyTarget(target);

    let schema: DataSchema;

    if (SchemaUtils.isCore(target.schema)) {
      schema = dataModel.schemas.product(target.schema.product)?.find(target.schema.name);
    }
    else if (SchemaUtils.isUserDataset(target.schema)) {
      schema = dataModel.schemas.userDataset(target.schema.userDatasetId)?.find(target.schema.name);
    }
    else if (SchemaUtils.isResultSet(target.schema)) {
      schema = dataModel.schemas.resultSet(target.schema.resultSetId)?.find(target.schema.name);
    }
    else
      throw new Error(`Unsupported schema target '${SchemaUtils.getTargetString(target.schema)}'`);

    if (!schema)
      return null;
      
    let group = schema.itemGroups.find(target.group);
    if (!group)
      return null;

    let fieldName = target.field.toLowerCase();

    let schemaItem = schema.items.find(target.group, target.field, target.childField);
    if (!schemaItem)
      schemaItem = tryGetFallBackItem(target, schema);

    if (!schemaItem) {
      if (fieldName.startsWith('udf__')) {
        // Legacy UDF handling. Try to find the UDF in model, then map to the schema item that references it
        schemaItem = group.items.firstMatch(x => x.field.name.toLowerCase() == fieldName);
      }
    }

    if (!schemaItem)
      schemaItem = tryGetFallBackItem(target, schema);

    if (!schemaItem)
      return null;

    let t = new SchemaItemFieldTarget(
      schemaItem,
      DataAggregation.from(target.aggregation));

    if (target.childField && !t.isChild) {
      t = t.withChildField(target.childField);
    }

    return t;
  }

  function tryGetFallBackItem(target: SchemaFieldTargetDto, schema: DataSchema) {
    if (!SchemaUtils.isUserDataset(target.schema) || !schema)
      return null;

    let table = schema.source.tables.find(`userdataset${target.schema.userDatasetId}`) ?? schema.source.tables.first();
    if (!table)
      return null;

    let modelField = table.fields.find(target.field);
    if (!modelField)
      return null;

    let group = schema.itemGroups.find(target.group);
    if (!group)
      return null;

    let schemaItem = group.items.firstMatch(x => x.field == modelField);
   
    return schemaItem;
  }

  export function mapLegacyTarget(t: SchemaFieldTargetDto): SchemaFieldTargetDto {
    t = {
      ...t,
      group: t.group?.toLowerCase(),
      field: t.field?.toLowerCase(),
      childField: t.childField?.toLowerCase()
    };

    if (t.childField)
      t.childField = mapChildName(t.childField);

    return t;
  }

  export function mapChildName(childField: string): string {
    if (childField) {
      if (childField.startsWith('__dd_')) {
        let f = childField.substring(5);
        if (f.startsWith('udf__')) {
          childField = f.substring(f.lastIndexOf('__') + 2);
        }
        else {
          childField = f.split('__', 2)[1];
        }
      }
    }

    return childField;
  }
}
